import { isNil, omitBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';

import { ordersApi, CancelFeedback } from 'api';
import { PaymentLayoutAction } from 'components';
import { EcomCardData } from 'components/EcomCardForm';
import {
  CascadeStatus,
  CustomerConfirmStatusDetails,
  EventName,
  NewStatusDetails,
  OrderStatus,
  PaymentType,
  StatusCode,
} from 'enums';
import { Order, PaymentFormValues, TradeMethod, UpdateOrderDto } from 'types';
import { eventUtils } from 'utils';

import { ApiActionOptions } from './useOrderData';

export type OrderViewActions = {
  cancel: PaymentLayoutAction;
  confirmCard: PaymentLayoutAction;
  confirmPayment: PaymentLayoutAction;
  backToShop: PaymentLayoutAction;
  selectTradeMethod: PaymentLayoutAction;
};

type UseOrderActionsInput = {
  orderId: string;
  order: Order | undefined;
  initialOrderFetched: boolean;
  paymentFormValues: PaymentFormValues;
  isCustomerDataConfirmed: boolean;
  customerDataBeforePayment: boolean;
  isCardInfoConfirmDisabled: boolean;
  setUploadReceiptModalOpen: (open: boolean) => void;
  setStartPaymentSyncFailed: (failed: boolean) => void;
  apiActionOptions: ApiActionOptions;
  actionLabels: Record<keyof OrderViewActions, string>;
};

export type UseOrderActionsResult = {
  actions: OrderViewActions;
  selectPaymentType: (tradeMethod: TradeMethod) => void;
  confirmCard: (
    formValues: PaymentFormValues,
    options?: { silent?: boolean },
  ) => void;
  confirmPayment: (orderId: string) => void;
  submitEcomCard: (card: EcomCardData) => void;
  submitEcomConfirmCode: (confirmCode: string) => void;
  isStartPaymentSyncAPICallInProgress: boolean;
  isStartRequisitesCascadeLoading: boolean;
  isEcomSubmitInProgress: boolean;
  isCancelLoading: boolean;
  cancelWithFeedback: (feedback?: CancelFeedback) => void;
};

// A cascade request that never reached the backend search leaves the order in New. A fast,
// transient failure of it is retried automatically this many times, CASCADE_AUTO_RETRY_DELAY_MS
// apart, before falling back to the trade-method picker.
const CASCADE_AUTO_RETRY_LIMIT = 2;
const CASCADE_AUTO_RETRY_DELAY_MS = 2000;

// Only a dropped connection (no response) or 502/503 is worth retrying. A 4xx (e.g. a disabled
// payment type) fails the same way every time, and a 504 means the proxy already waited out its
// whole timeout — retrying it would keep the customer on the loader for minutes.
const isCascadeErrorRetryable = (error: any) => {
  const status = error?.response?.status;
  return (
    !status ||
    status === StatusCode.BadGateway ||
    status === StatusCode.ServiceUnavailable
  );
};

export const useOrderActions = ({
  orderId,
  order,
  initialOrderFetched,
  paymentFormValues,
  isCustomerDataConfirmed,
  customerDataBeforePayment,
  isCardInfoConfirmDisabled,
  setUploadReceiptModalOpen,
  setStartPaymentSyncFailed,
  apiActionOptions,
  actionLabels,
}: UseOrderActionsInput): UseOrderActionsResult => {
  const { mutate: update } = useMutation(ordersApi.update, apiActionOptions);
  const {
    mutate: startPaymentSync,
    isLoading: isStartPaymentSyncAPICallInProgress,
  } = useMutation(ordersApi.startPaymentSync, apiActionOptions);
  const {
    mutate: startRequisitesCascade,
    isLoading: isCascadeMutationLoading,
  } = useMutation(ordersApi.startRequisitesCascade, apiActionOptions);

  const [cascadeStatus, setCascadeStatus] = useState<CascadeStatus>(
    CascadeStatus.Idle,
  );

  // Synchronous fire-once guard alongside cascadeStatus: React 18 StrictMode re-invokes a mount
  // effect against the same render's closure before this effect's own mutate() call has updated
  // isCascadeMutationLoading, so that alone can't stop a second invocation from firing a second
  // POST in that dev-only double-invoke window.
  // Invariant: cascadeFiredRef === false <=> cascadeStatus === Idle. Reset it only via
  // resetCascade(), never on its own — resetting just one of the two silently stops the cascade
  // from ever firing.
  const cascadeFiredRef = React.useRef(false);

  // The customer's manual trade-method pick started the payment (startPayment, or startPaymentSync
  // for sciCustomTradeMethodsEnabled shops) — the auto-cascade must not fire on top of it.
  const manualPaymentStartedRef = React.useRef(false);

  // Auto-retries spent on the current attempt; reset by NewTradeMethodReset together with the cascade.
  const cascadeAutoRetriesRef = React.useRef(0);

  // The manual trade-method-pick path below (update, startPayment, startPaymentSync — as opposed to
  // startRequisitesCascade) has no isLoading of its own tracked here, so a failure can't be told
  // apart from "still in flight" by cascadeStatus/canAutoCascade alone — both stay exactly as they
  // were the moment the attempt started. This flag is the only thing that flips once it fails.
  const [hasManualPaymentFailed, setHasManualPaymentFailed] = useState(false);

  const resetCascade = useCallback(() => {
    cascadeFiredRef.current = false;
    setCascadeStatus(CascadeStatus.Idle);
  }, []);

  // Shared by the auto-cascade effect below and hasFixedPaymentTypeAwaitingCascade further down —
  // kept as one predicate so the two can't drift out of sync with each other. initialOrderFetched
  // is deliberately not part of this: hasFixedPaymentTypeAwaitingCascade must stay true even before
  // the initial order fetch resolves, so the New-status screen never flashes during that wait either.
  const canAutoCascade =
    order?.status === OrderStatus.New &&
    !!order?.payment?.type &&
    // ecom starts payment sync only after the customer submits the card (submitEcomCard); it must not
    // auto-cascade requisites while the order is New, or it would leave New before the card is entered.
    order?.payment?.type !== PaymentType.Ecom &&
    // exception for RosaOne integration which finds requisites for order in New status
    order?.statusDetails !== NewStatusDetails.NewStartPaymentSyncAttempt &&
    !order?.requisites;

  useEffect(() => {
    // start finding requisites if the order was created with specified payment type.
    // Resetting cascadeStatus back to Idle (scheduled auto-retry, or NewTradeMethodReset below) makes
    // this effect re-run and re-fire the cascade on its own, since cascadeStatus is a dependency.
    if (
      cascadeStatus !== CascadeStatus.Idle ||
      cascadeFiredRef.current ||
      manualPaymentStartedRef.current ||
      !initialOrderFetched ||
      !canAutoCascade
    ) {
      return;
    }
    cascadeFiredRef.current = true;
    startRequisitesCascade(orderId, {
      onSuccess: () => setCascadeStatus(CascadeStatus.Done),
      // The cascade request can fail (e.g. a proxy timing out the connection) while the search actually
      // keeps running and succeeds on the backend, which already flipped the order out of New before the
      // search started. Once the mutation settles, its own isLoading drops immediately, but the local
      // order.status is still the stale "New" from before the call — so without this, the New-status
      // multiform screen renders for a frame until the compensating refetch (below) lands the real status.
      onError: (error: any) => {
        if (!isCascadeErrorRetryable(error)) {
          // spend the remaining budget so reconciliation goes straight to Failed
          cascadeAutoRetriesRef.current = CASCADE_AUTO_RETRY_LIMIT;
        }
        setCascadeStatus(CascadeStatus.Reconciling);
      },
    });
  }, [
    cascadeStatus,
    initialOrderFetched,
    canAutoCascade,
    orderId,
    startRequisitesCascade,
  ]);

  useEffect(() => {
    // initialOrderFetched flips back to true once the compensating refetch (triggered by
    // apiActionOptions.onError on the cascade failure above) lands a confirmed order status.
    if (!initialOrderFetched || cascadeStatus !== CascadeStatus.Reconciling) {
      return;
    }
    if (order?.status === OrderStatus.New && !order?.requisites) {
      // The refetched order is authoritative: the backend moves the order out of New before it
      // searches, so still being New means the search never ran. Retry it a bounded number of
      // times; after that drop the loader and leave the customer on the trade-method picker,
      // which is what a failed cascade showed before the loader covered this state.
      if (cascadeAutoRetriesRef.current < CASCADE_AUTO_RETRY_LIMIT) {
        cascadeAutoRetriesRef.current += 1;
        setCascadeStatus(CascadeStatus.RetryScheduled);
      } else {
        setCascadeStatus(CascadeStatus.Failed);
      }
    } else {
      // the backend search actually succeeded despite the proxy error — nothing to reconcile
      resetCascade();
    }
  }, [
    initialOrderFetched,
    cascadeStatus,
    order?.status,
    order?.requisites,
    resetCascade,
  ]);

  useEffect(() => {
    if (cascadeStatus !== CascadeStatus.RetryScheduled) {
      return undefined;
    }
    // back to Idle re-runs the auto-cascade effect, which fires the cascade again
    const timeoutId = setTimeout(resetCascade, CASCADE_AUTO_RETRY_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [cascadeStatus, resetCascade]);

  // The order is New with its payment type already fixed at creation — the auto-cascade effect
  // above is about to fire (or is between renders before it does), so there's nothing for the
  // customer to choose, regardless of whether the shop uses the custom trade-methods picker.
  // Treating this the same as "cascade loading" keeps the New-status multiform screen from ever
  // mounting for this order, instead of only being hidden once the mutation itself is in flight.
  // hasManualPaymentFailed excludes the case where the customer manually picked this same fixed
  // payment type and starting the payment itself failed — cascadeStatus/canAutoCascade don't
  // change in that case (this flow never touches startRequisitesCascade), so without it the
  // loader would stay stuck forever instead of handing the customer back to the
  // "select another method" screen.
  const hasFixedPaymentTypeAwaitingCascade =
    cascadeStatus === CascadeStatus.Idle &&
    canAutoCascade &&
    !hasManualPaymentFailed;

  const isStartRequisitesCascadeLoading =
    isCascadeMutationLoading ||
    cascadeStatus === CascadeStatus.Reconciling ||
    cascadeStatus === CascadeStatus.RetryScheduled ||
    hasFixedPaymentTypeAwaitingCascade;

  useEffect(() => {
    // resetTradeMethod puts the order back in New so the customer can pick a different trade method,
    // but manualPaymentStartedRef is a one-shot latch from the first attempt and won't reset itself —
    // without this, the guard above would stay permanently blocked and the retry would never auto-cascade.
    if (order?.statusDetails === NewStatusDetails.NewTradeMethodReset) {
      manualPaymentStartedRef.current = false;
      cascadeAutoRetriesRef.current = 0;
      setHasManualPaymentFailed(false);
      resetCascade();
    }
  }, [order?.statusDetails, resetCascade]);

  const { mutate: startPayment } = useMutation(
    ordersApi.startPayment,
    apiActionOptions,
  );
  const { mutate: confirmPayment } = useMutation(
    ordersApi.confirmPayment,
    apiActionOptions,
  );
  const { mutate: cancelMutate, isLoading: isCancelLoading } = useMutation(
    ({ id, feedback }: { id: string; feedback?: CancelFeedback }) =>
      ordersApi.cancel(id, feedback),
    apiActionOptions,
  );

  const {
    mutate: submitConfirmCodeMutate,
    isLoading: isSubmitConfirmCodeLoading,
  } = useMutation(ordersApi.submitConfirmCode, apiActionOptions);

  const submitEcomCard = useCallback(
    (card: EcomCardData) =>
      startPaymentSync(
        { id: orderId, data: { card } },
        {
          onError: (error: any) => {
            eventUtils.trackException({
              message: `submitEcomCard failed. ${
                error?.response?.data?.message || error?.message || ''
              }`,
            });
          },
        },
      ),
    [orderId, startPaymentSync],
  );

  const submitEcomConfirmCode = useCallback(
    (confirmCode: string) =>
      submitConfirmCodeMutate(
        { id: orderId, confirmCode },
        {
          onError: (error: any) => {
            eventUtils.trackException({
              message: `submitConfirmCode failed. ${
                error?.response?.data?.message || error?.message || ''
              }`,
            });
          },
        },
      ),
    [orderId, submitConfirmCodeMutate],
  );

  const isEcomSubmitInProgress =
    isStartPaymentSyncAPICallInProgress || isSubmitConfirmCodeLoading;

  const status = useMemo(() => order?.status as OrderStatus, [order]);
  const returnUrl = useMemo(
    () => order?.integration?.returnUrl,
    [order?.integration?.returnUrl],
  );
  const successUrl = useMemo(
    () => order?.integration?.successUrl || order?.integration?.returnUrl,
    [order?.integration],
  );
  const failUrl = useMemo(
    () => order?.integration?.failUrl || order?.integration?.returnUrl,
    [order?.integration],
  );

  const updatePaymentInfo = useCallback(
    (
      updateDto: UpdateOrderDto,
      {
        onSuccess,
        onError,
      }: { onSuccess?: () => void; onError?: () => void } = {},
    ) => {
      update(
        { id: orderId, data: updateDto },
        {
          onSuccess: (data) => {
            apiActionOptions.onSuccess(data);
            onSuccess?.();
          },
          onError: (data: any) => {
            eventUtils.trackException({
              message: `updatePaymentInfo failed. ${
                data?.response?.data?.message || data?.message || ''
              }`,
            });
            apiActionOptions.onError(data);
            onError?.();
          },
        },
      );
    },
    [orderId, update, apiActionOptions],
  );

  const navigateReturnUrl = useCallback(() => {
    const url =
      status === OrderStatus.Completed
        ? successUrl
        : status === OrderStatus.Cancelled
        ? failUrl
        : returnUrl;
    if (url) {
      window.location.href = url;
    }
  }, [status, returnUrl, successUrl, failUrl]);

  const selectPaymentType = useCallback(
    (tradeMethod: TradeMethod) => {
      eventUtils.trackEvent({
        event: EventName.SelectPaymentType,
        message: [tradeMethod.paymentType, tradeMethod.bank]
          .filter(Boolean)
          .join(' / '),
      });
      // ecom: don't start payment on selection. Just set the payment type so the order stays New and
      // the SCI shows the card form first. The card submit (submitEcomCard) then triggers startPaymentSync.
      // No bank — ecom is bankless; sending the any-bank placeholder code makes providers reject the order.
      if (tradeMethod.paymentType === PaymentType.Ecom) {
        updatePaymentInfo({ payment: { type: tradeMethod.paymentType } });
        return;
      }
      if (cascadeStatus === CascadeStatus.Failed) {
        // The auto-retries are used up and the customer picked a method from the picker. Back to
        // Idle so hasFixedPaymentTypeAwaitingCascade keeps the loader up (and the picker hidden)
        // while the manual attempt runs — the ref is set first so the auto-cascade doesn't fire
        // again on the old payment type once the status is Idle.
        manualPaymentStartedRef.current = true;
        resetCascade();
      }
      if (order?.shop?.sciCustomTradeMethodsEnabled) {
        // manual method sync owns the cascade; block the New-status auto-effect
        // from firing a second startRequisitesCascade if no requisites are found
        manualPaymentStartedRef.current = true;
        // Same as the branch below: the backend leaves the order New with payment.type already set
        // when no requisites are found, which satisfies canAutoCascade — hasManualPaymentFailed is
        // the only thing that lets hasFixedPaymentTypeAwaitingCascade drop the loader then.
        setHasManualPaymentFailed(false);
        startPaymentSync(
          {
            id: orderId,
            data: {
              payment: {
                type: tradeMethod.paymentType,
                bank: tradeMethod.bank,
              },
            },
          },
          {
            onSuccess: (responseOrder: Order) => {
              if (!responseOrder.requisites) {
                setHasManualPaymentFailed(true);
                setStartPaymentSyncFailed(true);
              }
            },
            onError: (error: any) => {
              eventUtils.trackException({
                message: `startPaymentSync failed. ${
                  error?.response?.data?.message || error?.message || ''
                }`,
              });
              setHasManualPaymentFailed(true);
              setStartPaymentSyncFailed(true);
            },
          },
        );
      } else {
        // Reset before update() rather than in its onSuccess: apiActionOptions.onSuccess
        // (called first) already writes payment.type into the order, which can satisfy
        // canAutoCascade one render before this flag would otherwise clear — flashing the
        // method picker for a frame instead of the loader.
        setHasManualPaymentFailed(false);
        updatePaymentInfo(
          {
            payment: { type: tradeMethod.paymentType, bank: tradeMethod.bank },
          },
          {
            onSuccess: () => {
              manualPaymentStartedRef.current = true;
              startPayment(orderId, {
                onError: (error: any) => {
                  eventUtils.trackException({
                    message: `startPayment failed. ${
                      error?.response?.data?.message || error?.message || ''
                    }`,
                  });
                  // The order stays New with its payment type already set — satisfies
                  // canAutoCascade, so hasFixedPaymentTypeAwaitingCascade must be told about
                  // this failure directly to drop the loader back to the trade-method picker.
                  // Not startPaymentSyncFailed: startPayment only moves the order to Requisites
                  // and never searches, so its failure (network, 5xx, validation) is not "no
                  // requisites found".
                  setHasManualPaymentFailed(true);
                },
              });
            },
            // The flag was cleared before update(); if update() itself fails, the order may still
            // carry payment.type from a previous failed attempt, so restore it to drop the loader.
            onError: () => setHasManualPaymentFailed(true),
          },
        );
      }
    },
    [
      cascadeStatus,
      resetCascade,
      orderId,
      order?.shop?.sciCustomTradeMethodsEnabled,
      startPayment,
      updatePaymentInfo,
      startPaymentSync,
      setStartPaymentSyncFailed,
    ],
  );

  const confirmCard = useCallback(
    (formValues: PaymentFormValues, options?: { silent?: boolean }) => {
      if (!options?.silent) {
        eventUtils.trackEvent({
          event: EventName.ConfirmCustomerDetails,
        });
      }
      updatePaymentInfo(
        { payment: omitBy(formValues, isNil) },
        customerDataBeforePayment
          ? {}
          : {
              onSuccess: () => {
                if (order?.shop?.collectCustomerReceipts) {
                  setUploadReceiptModalOpen(true);
                } else {
                  confirmPayment(orderId);
                }
              },
            },
      );
    },
    [
      confirmPayment,
      customerDataBeforePayment,
      orderId,
      updatePaymentInfo,
      order?.shop?.collectCustomerReceipts,
      setUploadReceiptModalOpen,
    ],
  );

  const actions: OrderViewActions = useMemo(
    () => ({
      cancel: {
        label: actionLabels.cancel,
        color: 'error' as const,
        onClick: () => {
          eventUtils.trackEvent({
            event: EventName.OrderCancel,
          });
          cancelMutate({ id: orderId });
        },
      },
      confirmCard: {
        label: actionLabels.confirmCard,
        color: 'primary' as const,
        disabled: isCardInfoConfirmDisabled,
        onClick: () => confirmCard(paymentFormValues),
      },
      confirmPayment: {
        label: actionLabels.confirmPayment,
        color: 'success' as const,
        onClick: () => {
          eventUtils.trackEvent({
            event: EventName.ConfirmTransfer,
          });
          updatePaymentInfo(
            {
              customerConfirmStatusDetails:
                CustomerConfirmStatusDetails.CustomerPayed,
            },
            customerDataBeforePayment || isCustomerDataConfirmed
              ? {
                  onSuccess: () => {
                    if (order?.shop?.collectCustomerReceipts) {
                      setUploadReceiptModalOpen(true);
                    } else {
                      confirmPayment(orderId);
                    }
                  },
                }
              : {},
          );
        },
      },
      backToShop: {
        color: 'tertiary' as const,
        label: actionLabels.backToShop,
        onClick: () => {
          eventUtils.trackEvent({
            event: EventName.BackToShop,
          });
          navigateReturnUrl();
        },
        hidden:
          (status === OrderStatus.Completed && !successUrl) ||
          (status === OrderStatus.Cancelled && !failUrl) ||
          !returnUrl,
      },
      selectTradeMethod: {
        label: actionLabels.selectTradeMethod,
        color: 'primary' as const,
        onClick: () => setStartPaymentSyncFailed(false),
      },
    }),
    [
      isCardInfoConfirmDisabled,
      status,
      returnUrl,
      successUrl,
      failUrl,
      cancelMutate,
      orderId,
      confirmCard,
      paymentFormValues,
      updatePaymentInfo,
      customerDataBeforePayment,
      isCustomerDataConfirmed,
      order?.shop?.collectCustomerReceipts,
      confirmPayment,
      navigateReturnUrl,
      setStartPaymentSyncFailed,
      setUploadReceiptModalOpen,
      actionLabels,
    ],
  );

  const cancelWithFeedback = useCallback(
    (feedback?: CancelFeedback) => {
      eventUtils.trackEvent({
        event: EventName.OrderCancel,
      });
      cancelMutate({ id: orderId, feedback });
    },
    [cancelMutate, orderId],
  );

  return {
    actions,
    selectPaymentType,
    confirmCard,
    confirmPayment,
    submitEcomCard,
    submitEcomConfirmCode,
    isStartPaymentSyncAPICallInProgress,
    isStartRequisitesCascadeLoading,
    isEcomSubmitInProgress,
    isCancelLoading,
    cancelWithFeedback,
  };
};
